import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    FiBookOpen,
    FiEye,
    FiFileText,
    FiLoader,
    FiRefreshCw,
    FiSearch,
    FiTrendingDown,
    FiTrendingUp,
    FiUsers,
} from "react-icons/fi";

import {
    useDispatch,
    useSelector,
} from "react-redux";

import {
    useHistory,
} from "react-router-dom";

import {
    fetchSupplierLedgerEntries,
    fetchSupplierLedgerSummary,
    fetchSupplierBalanceList,
    supplierLedgerEntriesSelector,
    supplierLedgerEntriesPaginationSelector,
    supplierLedgerSummarySelector,
    supplierBalanceListSelector,
    supplierBalancePaginationSelector,
    supplierLedgerLoadingSelector,
    supplierLedgerErrorSelector,
} from "../../../../../../store/billing/supplierLedger/supplierLedgerSlice";

import "./SupplierLedger.css";

/*
|--------------------------------------------------------------------------
| CONSTANTS
|--------------------------------------------------------------------------
*/

const PAGE_SIZE_OPTIONS = [
    10,
    20,
    50,
    100,
];

/*
|--------------------------------------------------------------------------
| HELPERS
|--------------------------------------------------------------------------
*/

const toNumber = (
    value,
    fallback = 0
) => {
    const number =
        Number(value);

    return Number.isFinite(
        number
    )
        ? number
        : fallback;
};

const roundMoney = (
    value
) =>
    Math.round(
        (
            toNumber(value) +
            Number.EPSILON
        ) *
            100
    ) / 100;

const formatCurrency = (
    value
) =>
    new Intl.NumberFormat(
        "en-IN",
        {
            style:
                "currency",

            currency:
                "INR",

            minimumFractionDigits:
                2,

            maximumFractionDigits:
                2,
        }
    ).format(
        roundMoney(value)
    );

const formatDate = (
    value
) => {
    if (!value) {
        return "-";
    }

    const date =
        new Date(value);

    if (
        Number.isNaN(
            date.getTime()
        )
    ) {
        return "-";
    }

    return new Intl.DateTimeFormat(
        "en-IN",
        {
            day:
                "2-digit",

            month:
                "short",

            year:
                "numeric",
        }
    ).format(
        date
    );
};

const formatLabel = (
    value
) =>
    String(
        value ||
            "-"
    )
        .replace(
            /_/g,
            " "
        )
        .toLowerCase()
        .replace(
            /\b\w/g,
            (
                character
            ) =>
                character.toUpperCase()
        );

const getSupplierName = (
    row
) =>
    row
        ?.supplierSnapshot
        ?.supplierName ||
    row
        ?.supplierSnapshot
        ?.companyName ||
    "Supplier";

const getSupplierMobile = (
    row
) =>
    row
        ?.supplierSnapshot
        ?.mobile ||
    "-";

/*
|--------------------------------------------------------------------------
| COMPONENT
|--------------------------------------------------------------------------
*/

const SupplierLedgerPage = () => {
    const dispatch =
        useDispatch();

    const history =
        useHistory();

    const entries =
        useSelector(
            supplierLedgerEntriesSelector
        ) || [];

    const entriesPagination =
        useSelector(
            supplierLedgerEntriesPaginationSelector
        ) || {};

    const summary =
        useSelector(
            supplierLedgerSummarySelector
        ) || {};

    const supplierBalances =
        useSelector(
            supplierBalanceListSelector
        ) || [];

    const supplierPagination =
        useSelector(
            supplierBalancePaginationSelector
        ) || {};

    const loading =
        useSelector(
            supplierLedgerLoadingSelector
        ) || {};

    const errors =
        useSelector(
            supplierLedgerErrorSelector
        ) || {};

    /*
    |--------------------------------------------------------------------------
    | TAB
    |--------------------------------------------------------------------------
    */

    const [
        activeTab,
        setActiveTab,
    ] = useState(
        "suppliers"
    );

    /*
    |--------------------------------------------------------------------------
    | FILTERS
    |--------------------------------------------------------------------------
    */

    const [
        search,
        setSearch,
    ] = useState("");

    const [
        transactionType,
        setTransactionType,
    ] = useState("");

    const [
        fromDate,
        setFromDate,
    ] = useState("");

    const [
        toDate,
        setToDate,
    ] = useState("");

    const [
        page,
        setPage,
    ] = useState(1);

    const [
        limit,
        setLimit,
    ] = useState(20);

    /*
    |--------------------------------------------------------------------------
    | LOAD SUMMARY
    |--------------------------------------------------------------------------
    */

    const loadSummary =
        useCallback(
            async () => {
                await dispatch(
                    fetchSupplierLedgerSummary({
                        search:
                            search.trim(),

                        transactionType,

                        fromDate,

                        toDate,
                    })
                );
            },
            [
                dispatch,
                search,
                transactionType,
                fromDate,
                toDate,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | LOAD SUPPLIER BALANCES
    |--------------------------------------------------------------------------
    */

    const loadSupplierBalances =
        useCallback(
            async () => {
                await dispatch(
                    fetchSupplierBalanceList({
                        page,

                        limit,

                        search:
                            search.trim(),

                        fromDate,

                        toDate,
                    })
                );
            },
            [
                dispatch,
                page,
                limit,
                search,
                fromDate,
                toDate,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | LOAD ALL ENTRIES
    |--------------------------------------------------------------------------
    */

    const loadEntries =
        useCallback(
            async () => {
                await dispatch(
                    fetchSupplierLedgerEntries({
                        page,

                        limit,

                        search:
                            search.trim(),

                        transactionType,

                        fromDate,

                        toDate,

                        sortBy:
                            "transactionDate",

                        sortOrder:
                            "desc",
                    })
                );
            },
            [
                dispatch,
                page,
                limit,
                search,
                transactionType,
                fromDate,
                toDate,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | FETCH
    |--------------------------------------------------------------------------
    */

    useEffect(
        () => {
            const timer =
                setTimeout(
                    () => {
                        loadSummary();

                        if (
                            activeTab ===
                            "suppliers"
                        ) {
                            loadSupplierBalances();
                        } else {
                            loadEntries();
                        }
                    },
                    350
                );

            return () =>
                clearTimeout(
                    timer
                );
        },
        [
            activeTab,
            loadSummary,
            loadSupplierBalances,
            loadEntries,
        ]
    );

    /*
    |--------------------------------------------------------------------------
    | SUMMARY
    |--------------------------------------------------------------------------
    */

    const normalizedSummary =
        useMemo(
            () => ({
                totalEntries:
                    toNumber(
                        summary
                            ?.totalEntries
                    ),

                purchaseBillAmount:
                    roundMoney(
                        summary
                            ?.purchaseBillAmount
                    ),

                paymentOutAmount:
                    roundMoney(
                        summary
                            ?.paymentOutAmount
                    ),

                debitNoteAmount:
                    roundMoney(
                        summary
                            ?.debitNoteAmount
                    ),

                outstandingPayable:
                    roundMoney(
                        summary
                            ?.outstandingPayable
                    ),
            }),
            [
                summary,
            ]
        );

    /*
    |--------------------------------------------------------------------------
    | VIEW SUPPLIER LEDGER
    |--------------------------------------------------------------------------
    */

    const handleViewSupplier = (
        row
    ) => {
        const supplierId =
            row?.supplier;

        if (!supplierId) {
            return;
        }

        history.push(
            `/billing/supplier-ledger/${supplierId}`
        );
    };

    /*
    |--------------------------------------------------------------------------
    | CLEAR FILTERS
    |--------------------------------------------------------------------------
    */

    const handleClearFilters =
        () => {
            setSearch(
                ""
            );

            setTransactionType(
                ""
            );

            setFromDate(
                ""
            );

            setToDate(
                ""
            );

            setPage(
                1
            );
        };

    /*
    |--------------------------------------------------------------------------
    | PAGINATION
    |--------------------------------------------------------------------------
    */

    const currentPagination =
        activeTab ===
        "suppliers"
            ? supplierPagination
            : entriesPagination;

    const totalRecords =
        toNumber(
            currentPagination
                ?.total
        );

    const totalPages =
        Math.max(
            toNumber(
                currentPagination
                    ?.totalPages,
                1
            ),
            1
        );

    const currentPage =
        Math.max(
            toNumber(
                currentPagination
                    ?.page,
                page
            ),
            1
        );

    const startRecord =
        totalRecords >
        0
            ? (
                  currentPage -
                  1
              ) *
                  limit +
              1
            : 0;

    const endRecord =
        Math.min(
            currentPage *
                limit,
            totalRecords
        );

    /*
    |--------------------------------------------------------------------------
    | REFRESH
    |--------------------------------------------------------------------------
    */

    const handleRefresh =
        async () => {
            await loadSummary();

            if (
                activeTab ===
                "suppliers"
            ) {
                await loadSupplierBalances();
            } else {
                await loadEntries();
            }
        };

    /*
    |--------------------------------------------------------------------------
    | RENDER
    |--------------------------------------------------------------------------
    */

    return (
        <div className="supplier-ledger-page">
            {/* HEADER */}

            <header className="supplier-ledger-header">
                <div className="supplier-ledger-heading">
                    <span className="supplier-ledger-heading-icon">
                        <FiBookOpen />
                    </span>

                    <div>
                        <h1>
                            Supplier Ledger
                        </h1>

                        <p>
                            Track supplier purchases, payments, debit notes and outstanding payable.
                        </p>
                    </div>
                </div>

                <button
                    type="button"
                    className="supplier-ledger-button"
                    disabled={
                        loading.summary ||
                        loading.suppliers ||
                        loading.entries
                    }
                    onClick={
                        handleRefresh
                    }
                >
                    <FiRefreshCw
                        className={
                            loading.summary ||
                            loading.suppliers ||
                            loading.entries
                                ? "supplier-ledger-spinner"
                                : ""
                        }
                    />

                    Refresh
                </button>
            </header>

            {/* SUMMARY */}

            <section className="supplier-ledger-summary-grid">
                <div className="supplier-ledger-summary-card">
                    <span>
                        Total Purchases
                    </span>

                    <strong>
                        {formatCurrency(
                            normalizedSummary
                                .purchaseBillAmount
                        )}
                    </strong>

                    <small>
                        Purchase bills posted
                    </small>
                </div>

                <div className="supplier-ledger-summary-card supplier-ledger-summary-payment">
                    <span>
                        Payments Made
                    </span>

                    <strong>
                        {formatCurrency(
                            normalizedSummary
                                .paymentOutAmount
                        )}
                    </strong>

                    <small>
                        Supplier payments
                    </small>
                </div>

                <div className="supplier-ledger-summary-card supplier-ledger-summary-debit">
                    <span>
                        Debit Notes
                    </span>

                    <strong>
                        {formatCurrency(
                            normalizedSummary
                                .debitNoteAmount
                        )}
                    </strong>

                    <small>
                        Purchase return adjustments
                    </small>
                </div>

                <div className="supplier-ledger-summary-card supplier-ledger-summary-balance">
                    <span>
                        Outstanding Payable
                    </span>

                    <strong>
                        {formatCurrency(
                            normalizedSummary
                                .outstandingPayable
                        )}
                    </strong>

                    <small>
                        Current supplier payable
                    </small>
                </div>
            </section>

            {/* TABS */}

            <section className="supplier-ledger-tabs">
                <button
                    type="button"
                    className={
                        activeTab ===
                        "suppliers"
                            ? "active"
                            : ""
                    }
                    onClick={() => {
                        setActiveTab(
                            "suppliers"
                        );

                        setPage(
                            1
                        );
                    }}
                >
                    <FiUsers />

                    Supplier Wise Ledger
                </button>

                <button
                    type="button"
                    className={
                        activeTab ===
                        "entries"
                            ? "active"
                            : ""
                    }
                    onClick={() => {
                        setActiveTab(
                            "entries"
                        );

                        setPage(
                            1
                        );
                    }}
                >
                    <FiFileText />

                    All Entries
                </button>
            </section>

            {/* FILTERS */}

            <section className="supplier-ledger-toolbar">
                <div className="supplier-ledger-search">
                    <FiSearch />

                    <input
                        type="text"
                        value={
                            search
                        }
                        placeholder={
                            activeTab ===
                            "suppliers"
                                ? "Search supplier, company, mobile, GSTIN..."
                                : "Search supplier, document, payment, debit note..."
                        }
                        onChange={(
                            event
                        ) => {
                            setPage(
                                1
                            );

                            setSearch(
                                event
                                    .target
                                    .value
                            );
                        }}
                    />
                </div>

                {activeTab ===
                    "entries" && (
                    <select
                        value={
                            transactionType
                        }
                        onChange={(
                            event
                        ) => {
                            setPage(
                                1
                            );

                            setTransactionType(
                                event
                                    .target
                                    .value
                            );
                        }}
                    >
                        <option value="">
                            All Transactions
                        </option>

                        <option value="PURCHASE_BILL">
                            Purchase Bill
                        </option>

                        <option value="PAYMENT_OUT">
                            Payment Out
                        </option>

                        <option value="DEBIT_NOTE">
                            Debit Note
                        </option>

                        <option value="OPENING_BALANCE">
                            Opening Balance
                        </option>

                        <option value="ADJUSTMENT">
                            Adjustment
                        </option>
                    </select>
                )}

                <input
                    type="date"
                    value={
                        fromDate
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(
                            1
                        );

                        setFromDate(
                            event
                                .target
                                .value
                        );
                    }}
                />

                <input
                    type="date"
                    value={
                        toDate
                    }
                    min={
                        fromDate ||
                        undefined
                    }
                    onChange={(
                        event
                    ) => {
                        setPage(
                            1
                        );

                        setToDate(
                            event
                                .target
                                .value
                        );
                    }}
                />

                <button
                    type="button"
                    className="supplier-ledger-button"
                    onClick={
                        handleClearFilters
                    }
                >
                    Clear
                </button>
            </section>

            {/* ERROR */}

            {(errors.summary ||
                errors.suppliers ||
                errors.entries) && (
                <div className="supplier-ledger-alert">
                    {errors.summary ||
                        errors.suppliers ||
                        errors.entries}
                </div>
            )}

            {/* SUPPLIER WISE */}

            {activeTab ===
                "suppliers" && (
                <section className="supplier-ledger-card">
                    {loading.suppliers &&
                    supplierBalances.length ===
                        0 ? (
                        <div className="supplier-ledger-empty">
                            <FiLoader className="supplier-ledger-spinner" />

                            <h3>
                                Loading suppliers...
                            </h3>
                        </div>
                    ) : supplierBalances.length ===
                      0 ? (
                        <div className="supplier-ledger-empty">
                            <FiUsers />

                            <h3>
                                No supplier ledger found
                            </h3>

                            <p>
                                Supplier balances will appear after purchase transactions are posted.
                            </p>
                        </div>
                    ) : (
                        <div className="supplier-ledger-table-wrapper">
                            <table className="supplier-ledger-table">
                                <thead>
                                    <tr>
                                        <th>
                                            Supplier
                                        </th>

                                        <th className="text-right">
                                            Purchases
                                        </th>

                                        <th className="text-right">
                                            Payments
                                        </th>

                                        <th className="text-right">
                                            Debit Notes
                                        </th>

                                        <th className="text-right">
                                            Balance
                                        </th>

                                        <th className="text-right">
                                            Entries
                                        </th>

                                        <th>
                                            Last Activity
                                        </th>

                                        <th className="text-right">
                                            Action
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {supplierBalances.map(
                                        (
                                            row,
                                            index
                                        ) => (
                                            <tr
                                                key={
                                                    row
                                                        ?.supplier ||
                                                    index
                                                }
                                            >
                                                <td>
                                                    <div className="supplier-ledger-supplier">
                                                        <strong>
                                                            {getSupplierName(
                                                                row
                                                            )}
                                                        </strong>

                                                        <span>
                                                            {getSupplierMobile(
                                                                row
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>

                                                <td className="text-right supplier-ledger-credit">
                                                    {formatCurrency(
                                                        row
                                                            ?.totalPurchases
                                                    )}
                                                </td>

                                                <td className="text-right supplier-ledger-debit">
                                                    {formatCurrency(
                                                        row
                                                            ?.totalPayments
                                                    )}
                                                </td>

                                                <td className="text-right supplier-ledger-debit">
                                                    {formatCurrency(
                                                        row
                                                            ?.totalDebitNotes
                                                    )}
                                                </td>

                                                <td className="text-right">
                                                    <strong
                                                        className={
                                                            toNumber(
                                                                row
                                                                    ?.balance
                                                            ) >
                                                            0
                                                                ? "supplier-ledger-balance-payable"
                                                                : "supplier-ledger-balance-clear"
                                                        }
                                                    >
                                                        {formatCurrency(
                                                            row
                                                                ?.balance
                                                        )}
                                                    </strong>
                                                </td>

                                                <td className="text-right">
                                                    {toNumber(
                                                        row
                                                            ?.transactionCount
                                                    )}
                                                </td>

                                                <td>
                                                    {formatDate(
                                                        row
                                                            ?.lastTransactionDate
                                                    )}
                                                </td>

                                                <td className="text-right">
                                                    <button
                                                        type="button"
                                                        className="supplier-ledger-view-button"
                                                        onClick={() =>
                                                            handleViewSupplier(
                                                                row
                                                            )
                                                        }
                                                    >
                                                        <FiEye />

                                                        View Ledger
                                                    </button>
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <footer className="supplier-ledger-pagination">
                        <div>
                            <span>
                                Showing{" "}
                                <strong>
                                    {startRecord}
                                </strong>
                                {" - "}
                                <strong>
                                    {endRecord}
                                </strong>
                                {" of "}
                                <strong>
                                    {totalRecords}
                                </strong>
                            </span>

                            <select
                                value={
                                    limit
                                }
                                onChange={(
                                    event
                                ) => {
                                    setPage(
                                        1
                                    );

                                    setLimit(
                                        Number(
                                            event
                                                .target
                                                .value
                                        )
                                    );
                                }}
                            >
                                {PAGE_SIZE_OPTIONS.map(
                                    (
                                        size
                                    ) => (
                                        <option
                                            key={
                                                size
                                            }
                                            value={
                                                size
                                            }
                                        >
                                            {size}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>

                        <div>
                            <button
                                type="button"
                                disabled={
                                    currentPage <=
                                        1 ||
                                    loading.suppliers
                                }
                                onClick={() =>
                                    setPage(
                                        Math.max(
                                            currentPage -
                                                1,
                                            1
                                        )
                                    )
                                }
                            >
                                Previous
                            </button>

                            <span>
                                Page{" "}
                                <strong>
                                    {currentPage}
                                </strong>{" "}
                                of{" "}
                                <strong>
                                    {totalPages}
                                </strong>
                            </span>

                            <button
                                type="button"
                                disabled={
                                    currentPage >=
                                        totalPages ||
                                    loading.suppliers
                                }
                                onClick={() =>
                                    setPage(
                                        Math.min(
                                            currentPage +
                                                1,
                                            totalPages
                                        )
                                    )
                                }
                            >
                                Next
                            </button>
                        </div>
                    </footer>
                </section>
            )}

            {/* ALL ENTRIES */}

            {activeTab ===
                "entries" && (
                <section className="supplier-ledger-card">
                    {loading.entries &&
                    entries.length ===
                        0 ? (
                        <div className="supplier-ledger-empty">
                            <FiLoader className="supplier-ledger-spinner" />

                            <h3>
                                Loading ledger entries...
                            </h3>
                        </div>
                    ) : entries.length ===
                      0 ? (
                        <div className="supplier-ledger-empty">
                            <FiFileText />

                            <h3>
                                No ledger entries found
                            </h3>
                        </div>
                    ) : (
                        <div className="supplier-ledger-table-wrapper">
                            <table className="supplier-ledger-table">
                                <thead>
                                    <tr>
                                        <th>
                                            Date
                                        </th>

                                        <th>
                                            Supplier
                                        </th>

                                        <th>
                                            Transaction
                                        </th>

                                        <th>
                                            Document
                                        </th>

                                        <th>
                                            Narration
                                        </th>

                                        <th className="text-right">
                                            Debit
                                        </th>

                                        <th className="text-right">
                                            Credit
                                        </th>

                                        <th className="text-right">
                                            Balance
                                        </th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {entries.map(
                                        (
                                            entry,
                                            index
                                        ) => (
                                            <tr
                                                key={
                                                    entry
                                                        ?._id ||
                                                    index
                                                }
                                            >
                                                <td>
                                                    {formatDate(
                                                        entry
                                                            ?.transactionDate
                                                    )}
                                                </td>

                                                <td>
                                                    <div className="supplier-ledger-supplier">
                                                        <strong>
                                                            {getSupplierName(
                                                                entry
                                                            )}
                                                        </strong>

                                                        <span>
                                                            {getSupplierMobile(
                                                                entry
                                                            )}
                                                        </span>
                                                    </div>
                                                </td>

                                                <td>
                                                    <span className="supplier-ledger-type">
                                                        {formatLabel(
                                                            entry
                                                                ?.transactionType
                                                        )}
                                                    </span>
                                                </td>

                                                <td>
                                                    {entry
                                                        ?.documentNumber ||
                                                        entry
                                                            ?.paymentNumber ||
                                                        entry
                                                            ?.debitNoteNumber ||
                                                        "-"}
                                                </td>

                                                <td>
                                                    {entry
                                                        ?.narration ||
                                                        "-"}
                                                </td>

                                                <td className="text-right supplier-ledger-debit">
                                                    {toNumber(
                                                        entry
                                                            ?.debit
                                                    ) >
                                                    0
                                                        ? formatCurrency(
                                                            entry
                                                                ?.debit
                                                        )
                                                        : "-"}
                                                </td>

                                                <td className="text-right supplier-ledger-credit">
                                                    {toNumber(
                                                        entry
                                                            ?.credit
                                                    ) >
                                                    0
                                                        ? formatCurrency(
                                                            entry
                                                                ?.credit
                                                        )
                                                        : "-"}
                                                </td>

                                                <td className="text-right">
                                                    <strong>
                                                        {formatCurrency(
                                                            entry
                                                                ?.closingBalance ??
                                                            entry
                                                                ?.runningBalance
                                                        )}
                                                    </strong>
                                                </td>
                                            </tr>
                                        )
                                    )}
                                </tbody>
                            </table>
                        </div>
                    )}

                    <footer className="supplier-ledger-pagination">
                        <div>
                            <span>
                                Showing{" "}
                                <strong>
                                    {startRecord}
                                </strong>
                                {" - "}
                                <strong>
                                    {endRecord}
                                </strong>
                                {" of "}
                                <strong>
                                    {totalRecords}
                                </strong>
                            </span>

                            <select
                                value={
                                    limit
                                }
                                onChange={(
                                    event
                                ) => {
                                    setPage(
                                        1
                                    );

                                    setLimit(
                                        Number(
                                            event
                                                .target
                                                .value
                                        )
                                    );
                                }}
                            >
                                {PAGE_SIZE_OPTIONS.map(
                                    (
                                        size
                                    ) => (
                                        <option
                                            key={
                                                size
                                            }
                                            value={
                                                size
                                            }
                                        >
                                            {size}
                                        </option>
                                    )
                                )}
                            </select>
                        </div>

                        <div>
                            <button
                                type="button"
                                disabled={
                                    currentPage <=
                                        1 ||
                                    loading.entries
                                }
                                onClick={() =>
                                    setPage(
                                        Math.max(
                                            currentPage -
                                                1,
                                            1
                                        )
                                    )
                                }
                            >
                                Previous
                            </button>

                            <span>
                                Page{" "}
                                <strong>
                                    {currentPage}
                                </strong>{" "}
                                of{" "}
                                <strong>
                                    {totalPages}
                                </strong>
                            </span>

                            <button
                                type="button"
                                disabled={
                                    currentPage >=
                                        totalPages ||
                                    loading.entries
                                }
                                onClick={() =>
                                    setPage(
                                        Math.min(
                                            currentPage +
                                                1,
                                            totalPages
                                        )
                                    )
                                }
                            >
                                Next
                            </button>
                        </div>
                    </footer>
                </section>
            )}
        </div>
    );
};

export default SupplierLedgerPage;